/**
 * WishlistIcon.tsx — Wishlist Header Icon
 * 
 * Shows wishlist item count from the wishlist store.
 * Navigates to wishlist page on click.
 * Reads pill styling settings from the layout preset for consistent header appearance.
 */
import React, { useEffect, useState } from 'react';

interface WishlistIconProps {
  data: {
    [key: string]: unknown;
  };
  styles?: string;
  variant?: string;
}

export const WishlistIcon: React.FC<WishlistIconProps> = ({ data, styles, variant: _variant }) => {
  const [count, setCount] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    // Read wishlist count from localStorage
    try {
      const wishlist = JSON.parse(localStorage.getItem('wishlist_items') ?? '[]') as unknown[];
      setCount(wishlist.length);
    } catch { /* empty */ }

    // Listen for wishlist updates
    interface WishlistUpdateDetail { count?: number }
    const handleWishlistChange = (e: Event) => {
      setCount((e as CustomEvent<WishlistUpdateDetail>).detail?.count ?? 0);
    };
    window.addEventListener('wishlist-update', handleWishlistChange);
    return () => window.removeEventListener('wishlist-update', handleWishlistChange);
  }, []);

  interface WishlistIconSettings {
    pillBackground?: string;
    pillBorder?: string;
    pillRadius?: string;
    pillPadding?: string;
    iconSize?: number;
    color?: string;
  }
  // Read pill styling from settings (matching MiniCart / MiniQuoteCart pattern)
  const dataSettings = data?.settings;
  const settings = (typeof dataSettings === 'object' && dataSettings !== null
    ? dataSettings
    : (data ?? {})) as WishlistIconSettings;
  const {
    // pillBackground intentionally NOT destructured/used: matching MiniCart, the
    // resting fill comes from the .header-icon-btn class reading
    // --color-header-icon-bg via the :root cascade. Setting it inline here
    // re-defines the var as itself → circular ref → transparent (see MiniCart's
    // note), and also froze the hover state. So we only set border/radius/padding.
    pillBorder = '1px solid var(--color-header-icon-border, var(--color-header-search-border, var(--glass-border, rgba(255,255,255,0.18))))',
    pillRadius = 'var(--style-btn-radius, 9999px)',
    pillPadding = '4px 14px',
    iconSize,
    color = '',
  } = settings;

  // Variant: icon-only (default) | icon-badge | icon-text-pill
  const variantRaw = (data as { variant?: string; content?: { variant?: string } } | undefined)?.variant
    ?? (data as { content?: { variant?: string } } | undefined)?.content?.variant
    ?? 'icon-only';
  const variant: 'icon-only' | 'icon-badge' | 'icon-text-pill' =
    ['icon-only','icon-badge','icon-text-pill'].includes(variantRaw)
      ? variantRaw as 'icon-only' | 'icon-badge' | 'icon-text-pill'
      : 'icon-only';

  const pillStyle: React.CSSProperties = {
    // IDENTICAL pattern to MiniCart / MiniQuoteCart (the icons that hover
    // correctly): set `border` directly inline and DO NOT set
    // --color-header-icon-bg inline (that var defaults to itself → circular
    // ref → transparent; .header-icon-btn reads it from :root via the normal
    // cascade). The prior version routed the border through a bespoke
    // --color-header-icon-border-color variable that the .header-icon-btn CSS
    // never reads (it reads --color-header-icon-border, no "-color"), so the
    // resting box never cleared on hover. Matching the cart's exact shape is
    // the fix — the class's :hover rule then flips bg + border-color uniformly.
    border: pillBorder,
    borderRadius: pillRadius,
    padding: pillPadding,
    minHeight: '32px',
    ...(color ? { ['--color-header-icon' as string]: color } : {}),
  };

  // Use 0 during SSR/hydration to match server HTML
  const displayCount = mounted ? count : 0;

  const isTextPill = variant === 'icon-text-pill';
  const showBadge = variant !== 'icon-only' && displayCount > 0;
  const pillStyleAdjusted = isTextPill
    ? { ...pillStyle, padding: '6px 14px', gap: '8px' }
    : pillStyle;

  return (
    <div className={`relative ${styles ?? ''}`} data-variant={variant}>
      <a
        href="/account/wishlist"
        className="relative flex items-center justify-center transition-all duration-200 header-icon-btn"
        style={{ ...pillStyleAdjusted, textDecoration: 'none' }}
        aria-label={`Wishlist with ${displayCount} items`}
      >
        <svg
          style={{
            width: iconSize ? `${iconSize}px` : 'var(--style-header-icon-size, 22px)',
            height: iconSize ? `${iconSize}px` : 'var(--style-header-icon-size, 22px)',
          }}
          fill="none" viewBox="0 0 24 24" stroke="var(--icon-color, currentColor)" strokeWidth={1.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
        </svg>
        {/* Text-pill variant: inline label that hides on mobile (mobile falls
            back to icon-only via responsive class). */}
        {isTextPill && (
          <span className="hidden sm:inline text-[13px] font-medium whitespace-nowrap">
            {displayCount > 0 ? `Saved · ${displayCount > 99 ? '99+' : displayCount}` : 'Saved'}
          </span>
        )}
        {/* Badge variant: count badge in the corner. icon-text-pill folds the
            count into the label so doesn't show a badge. */}
        {variant === 'icon-badge' && showBadge && (
          <span className="absolute -top-1.5 -right-1.5 font-extrabold text-[10px] rounded-full w-5 h-5 flex items-center justify-center border-2"
                style={{
                  backgroundColor: 'var(--color-global-badge-bg, var(--accent-primary))',
                  color: 'var(--color-global-badge-text, #ffffff)',
                  borderColor: 'var(--color-footer-bg, #1a1a1a)',
                }}>
            {displayCount > 99 ? '99+' : displayCount}
          </span>
        )}
      </a>
    </div>
  );
};

export default WishlistIcon;
